import React, { useEffect, useMemo, useState } from "react";

const CIRCLE_META = {
  1: { title: "ראשון", color: "#007bff", minRatio: 0.9, maxRatio: 1 },
  2: { title: "שני", color: "#28a745", minRatio: 0.8, maxRatio: 0.9 },
  3: { title: "שלישי", color: "#ff9800", minRatio: 0.6, maxRatio: 0.8 },
};

export const getAiCategories = (guests, { onlyUnseated = false } = {}) => {
  const counts = new Map();
  Object.values(guests || {}).forEach((guest) => {
    const name = String(guest?.closeness || "").trim();
    if (!name || !(parseInt(guest.accept) > 0)) return;
    if (onlyUnseated && parseInt(guest.table) > 0) return;
    counts.set(name, (counts.get(name) || 0) + parseInt(guest.accept));
  });
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name, "he"));
};

const formatTableRange = (nums) => {
  const sorted = [...new Set(nums)].sort((a, b) => a - b);
  if (!sorted.length) return "";
  const parts = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const n = sorted[i];
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    parts.push(start === prev ? String(start) : `${start}-${prev}`);
    start = n;
    prev = n;
  }
  return parts.join(", ");
};

const chipStyle = {
  background: "#fff",
  border: "1px solid #c5d0e6",
  borderRadius: 8,
  padding: "8px 10px",
  cursor: "grab",
  fontSize: 14,
  textAlign: "right",
  userSelect: "none",
  touchAction: "none",
};

const AiSeating = ({
  step,
  circles,
  setCircles,
  categoryTables,
  currentPick,
  pickIndex,
  pickCount,
  isRevisit,
  revisitLeft,
  upcoming,
  bundled,
  onAddToSeating,
  onRemoveFromSeating,
  onAdvancePick,
  onFinish,
  onBack,
  onClose,
  guests,
  tablesArray,
  allowedCategories,
  onlyUnseated,
}) => {
  const [drag, setDrag] = useState(null);
  const categories = useMemo(() => {
    const all = getAiCategories(guests, { onlyUnseated });
    if (!allowedCategories) return all;
    const allowed = new Set(allowedCategories);
    return all.filter((item) => allowed.has(item.name));
  }, [guests, allowedCategories, onlyUnseated]);
  const assigned = new Set([1, 2, 3].flatMap((circle) => circles[circle] || []));
  const pool = categories.filter((item) => !assigned.has(item.name));

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      setDrag((prev) =>
        prev ? { ...prev, x: e.clientX, y: e.clientY } : prev,
      );
    };
    const up = (e) => {
      const zone = document
        .elementFromPoint(e.clientX, e.clientY)
        ?.closest?.("[data-ai-drop]")
        ?.getAttribute("data-ai-drop");
      if (zone) {
        setCircles((prev) => {
          const next = { 1: [], 2: [], 3: [] };
          [1, 2, 3].forEach((circle) => {
            next[circle] = (prev[circle] || []).filter(
              (name) => name !== drag.name,
            );
          });
          if (zone === "1" || zone === "2" || zone === "3") {
            next[Number(zone)] = [...next[Number(zone)], drag.name];
          }
          return next;
        });
      }
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag?.name, setCircles]);

  const startDrag = (e, name) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ name, x: e.clientX, y: e.clientY });
  };

  if (step === "tables" && currentPick) {
    const selected = categoryTables[currentPick.category] || [];
    const meta = CIRCLE_META[currentPick.circle];
    const circleCategories = circles[currentPick.circle] || [];
    const indexInCircle = circleCategories.indexOf(currentPick.category);
    const companions = Object.entries(bundled || {})
      .filter(([, lead]) => lead === currentPick.category)
      .map(([name]) => name);
    const group = [currentPick.category, ...companions];
    const groupCounts = group.map((name) => ({
      name,
      count: categories.find((item) => item.name === name)?.count || 0,
    }));
    const confirmed = groupCounts.reduce((sum, item) => sum + item.count, 0);
    const selectedSeats = selected.reduce((sum, tableNum) => {
      const table = tablesArray?.[tableNum - 1];
      return sum + (parseInt(table?.amount) || 0);
    }, 0);
    const minSeats = Math.round(confirmed * meta.minRatio);
    const maxSeats = Math.round(confirmed * meta.maxRatio);
    const seatsColor =
      selectedSeats >= minSeats && selectedSeats <= maxSeats
        ? "#2e7d32"
        : selectedSeats > maxSeats
          ? "#e65100"
          : "#37474f";
    return (
      <div
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(380px, 40vw)",
          height: "100%",
          zIndex: 10002,
          background: "#fff",
          boxShadow: "-4px 0 18px rgba(0,0,0,0.15)",
          padding: "20px 16px",
          direction: "rtl",
          overflow: "auto",
          boxSizing: "border-box",
          textAlign: "right",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, color: meta.color }}>
          מעגל {meta.title}
        </div>
        <div style={{ fontSize: 13, color: "#607d8b", margin: "4px 0 8px" }}>
          קטגוריה {indexInCircle + 1} מתוך {circleCategories.length}
        </div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
          {groupCounts.map((item) => item.name).join(" + ")}
        </div>
        {companions.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            {groupCounts.map((item) => (
              <div
                key={item.name}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  padding: "4px 0",
                }}
              >
                <span>
                  {item.name} · {item.count}
                </span>
                {item.name !== currentPick.category && (
                  <button
                    type="button"
                    onClick={() => onRemoveFromSeating(item.name)}
                    style={{ ...ghostBtn, padding: "2px 8px", fontSize: 12 }}
                  >
                    הסרה
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {isRevisit && (
          <div style={{ fontSize: 13, color: "#e65100", marginBottom: 8 }}>
            שולחן פונה מהקטגוריה הזו. בחר שולחן אחר.
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
          אישרו: {confirmed}
        </div>
        <div style={{ fontSize: 14, marginBottom: 4 }}>
          מומלץ: {minSeats}–{maxSeats} מקומות ({Math.round(meta.minRatio * 100)}%–
          {Math.round(meta.maxRatio * 100)}%)
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: seatsColor, marginBottom: 4 }}>
          נבחרו: {selectedSeats} מקומות
        </div>
        <div style={{ fontSize: 13, color: "#607d8b", marginBottom: 10 }}>
          {selected.length
            ? `שולחנות ${formatTableRange(selected)}`
            : "עדיין לא נבחרו שולחנות"}
        </div>
        <div
          style={{
            marginTop: 8,
            paddingTop: 12,
            borderTop: "1px solid #eceff1",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>נותרו בתור</div>
          {(upcoming || []).length === 0 ? (
            <div style={{ fontSize: 13, color: "#90a4ae" }}>
              אין קטגוריות נוספות
            </div>
          ) : (
            (upcoming || []).map((item) => {
              const count =
                categories.find((category) => category.name === item.category)
                  ?.count || 0;
              const itemMeta = CIRCLE_META[item.circle];
              return (
                <div
                  key={`${item.circle}-${item.category}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    fontSize: 14,
                    padding: "6px 0",
                    borderBottom: "1px solid #f4f6f8",
                  }}
                >
                  <span style={{ flex: 1 }}>
                    <span style={{ color: itemMeta.color, fontWeight: 700 }}>
                      {itemMeta.title}
                    </span>{" "}
                    {item.category}
                    <span style={{ fontWeight: 700 }}> · {count}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onAddToSeating(item.category)}
                    style={{
                      ...primaryBtn,
                      padding: "4px 8px",
                      fontSize: 12,
                      whiteSpace: "nowrap",
                    }}
                  >
                    הוספה לשיבוץ
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
          <button type="button" onClick={onAdvancePick} style={primaryBtn}>
            {isRevisit
              ? revisitLeft > 0
                ? "לקטגוריה הבאה"
                : "סיום"
              : pickIndex >= pickCount - 1
                ? revisitLeft > 0
                  ? "לקטגוריה שפונתה"
                  : "סיום"
                : (upcoming || []).length === 0
                  ? "למעגל הבא"
                  : "לקטגוריה הבאה"}
          </button>
          <button type="button" onClick={onFinish} style={ghostBtn}>
            סיום
          </button>
          <button type="button" onClick={onBack} style={ghostBtn}>
            חזרה
          </button>
          <button type="button" onClick={onClose} style={dangerBtn}>
            ביטול
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10002,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        direction: "rtl",
      }}
    >
      <div
        style={{
          width: "min(920px, 94vw)",
          maxHeight: "88vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: 12,
          padding: 20,
          boxShadow: "0 8px 28px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 20 }}>הושבה AI</div>
          <button type="button" onClick={onClose} style={ghostBtn}>
            סגירה
          </button>
        </div>

        {step === "circles" && (
          <>
            <div style={{ fontSize: 14, color: "#455a64", marginBottom: 12 }}>
              {onlyUnseated
                ? "מוצגות רק הקטגוריות שעדיין לא הושבו. גרור כל אחת למעגל המתאים"
                : "גרור כל קטגוריה למעגל המתאים"}
            </div>
            <div
              data-ai-drop="pool"
              style={{
                minHeight: 64,
                border: "1px dashed #c5d0e6",
                borderRadius: 8,
                padding: 10,
                marginBottom: 16,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                background: "#f7f9fc",
              }}
            >
              {pool.length === 0 && (
                <span style={{ color: "#90a4ae", fontSize: 13 }}>
                  כל הקטגוריות שובצו
                </span>
              )}
              {pool.map((item) => (
                <div
                  key={item.name}
                  onPointerDown={(e) => startDrag(e, item.name)}
                  style={chipStyle}
                >
                  {item.name}
                  <span style={{ color: "#78909c" }}> ({item.count})</span>
                </div>
              ))}
            </div>
            <div style={{ fontWeight: 700, textAlign: "center", marginBottom: 8 }}>
              מעגלים
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
              }}
            >
              {[1, 2, 3].map((circle) => (
                <div
                  key={circle}
                  data-ai-drop={String(circle)}
                  style={{
                    minHeight: 180,
                    border: `2px dashed ${CIRCLE_META[circle].color}`,
                    borderRadius: 8,
                    padding: 8,
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: CIRCLE_META[circle].color,
                      textAlign: "center",
                      marginBottom: 8,
                    }}
                  >
                    {CIRCLE_META[circle].title}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(circles[circle] || []).map((name) => (
                      <div
                        key={name}
                        onPointerDown={(e) => startDrag(e, name)}
                        style={chipStyle}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                disabled={assigned.size === 0}
                onClick={onAdvancePick}
                style={{
                  ...primaryBtn,
                  opacity: assigned.size === 0 ? 0.5 : 1,
                }}
              >
                המשך לבחירת שולחנות
              </button>
              <button type="button" onClick={onFinish} style={ghostBtn}>
                סיום
              </button>
            </div>
          </>
        )}

      </div>
      {drag && (
        <div
          style={{
            ...chipStyle,
            position: "fixed",
            left: drag.x + 8,
            top: drag.y + 8,
            zIndex: 10003,
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {drag.name}
        </div>
      )}
    </div>
  );
};

const primaryBtn = {
  background: "#007bff",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 14,
};

const ghostBtn = {
  background: "#eceff1",
  color: "#37474f",
  border: "none",
  borderRadius: 6,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 14,
};

const dangerBtn = {
  background: "#dc3545",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 14,
};

export const AI_CIRCLE_COLORS = {
  1: "#007bff",
  2: "#28a745",
  3: "#ff9800",
};

export default AiSeating;
